import { createSlice } from '@reduxjs/toolkit'
import { GbcUserState, UserRole } from '../lib/types/userTypes'
import {
  setGbcUserDataToLS,
  removeGbcUserDataFromLS,
  getGbcUserDataFromLS,
} from '../lib/utils/local-storage'
import { normalizePermissionsFromLogin } from '../lib/permissions/adminFeatures'
import { normalizeActionGrantsFromLogin } from '../lib/permissions/adminActions'

const initialData: GbcUserState = {
  authToken: '',
  email: '',
  userId: 0,
  role: UserRole.CREDIBILA_ADMIN,
  isEmailVerified: false,
  permissions: normalizePermissionsFromLogin(UserRole.CREDIBILA_ADMIN, null),
  actionGrants: normalizeActionGrantsFromLogin(UserRole.CREDIBILA_ADMIN, null),
}

const getInitialData = () => {
  const { authToken, gbcUserData } = getGbcUserDataFromLS()
  if (!!authToken && !!gbcUserData) {
    const role = gbcUserData.role as UserRole
    return {
      authToken,
      email: gbcUserData.email,
      userId: gbcUserData.userId,
      role,
      isEmailVerified: gbcUserData.isEmailVerified,
      permissions: gbcUserData.permissions
        ? normalizePermissionsFromLogin(gbcUserData.role as string, gbcUserData.permissions)
        : normalizePermissionsFromLogin(gbcUserData.role as string, null),
      actionGrants: gbcUserData.actionGrants
        ? normalizeActionGrantsFromLogin(
            gbcUserData.role as string,
            gbcUserData.actionGrants as Record<string, unknown>,
          )
        : normalizeActionGrantsFromLogin(gbcUserData.role as string, null),
    }
  }
  return initialData
}

const initialState = getInitialData()

const gbcUserSlice = createSlice({
  name: 'gbcUser',
  initialState,
  reducers: {
    createConfiguration: (state: GbcUserState, action: { payload: GbcUserState }) => {
      state.authToken = action.payload.authToken
      state.email = action.payload.email
      state.userId = action.payload.userId
      state.role = action.payload.role
      state.isEmailVerified = action.payload.isEmailVerified
      state.permissions =
        action.payload.permissions ??
        normalizePermissionsFromLogin(action.payload.role as string, null)
      state.actionGrants =
        action.payload.actionGrants ??
        normalizeActionGrantsFromLogin(action.payload.role as string, null)

      const { authToken, gbcUserData } = getGbcUserDataFromLS()
      if (!authToken && !gbcUserData) setGbcUserDataToLS(state)
    },
    resetConfiguration: () => {
      removeGbcUserDataFromLS()
      return { ...initialData }
    },
  },
})

export { gbcUserSlice }
export const { createConfiguration, resetConfiguration } = gbcUserSlice.actions

export default gbcUserSlice.reducer
